function clean(value, max = 160) {
  return String(value || '').replace(/\u0000/g, '').trim().slice(0, max);
}

function decodedPath(urlValue) {
  try {
    return decodeURIComponent(new URL(urlValue).pathname || '');
  } catch (_) {
    return '';
  }
}

function normalizedLines(text) {
  return String(text || '')
    .replace(/\r/g, '\n')
    .split(/\n+/)
    .map(line => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
}

function wildfireIncidentFromUrl(value) {
  const path = decodedPath(value);
  const match = path.match(/\/([A-Z]{2}-[A-Z0-9]{2,8}-\d{4,9})_([^/]+)(?:\/|$)/i);
  if (!match) return null;
  return {
    incidentOrder: match[1].toUpperCase(),
    incidentName: clean(match[2].replace(/[_-]+/g, ' ').replace(/\s+/g, ' '), 100),
  };
}

function incidentOrderFromText(text) {
  const match = String(text || '').match(/\b([A-Z]{2}-[A-Z0-9]{2,8}-\d{4,9})\b/i);
  return match ? match[1].toUpperCase() : '';
}

function incidentNameFromText(text) {
  const lines = normalizedLines(text);
  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index];
    const direct = line.match(/INCIDENT\s+NAME\s*[:#-]?\s*(.{3,80})$/i);
    if (direct?.[1] && !/OPERATIONAL|DATE|TIME|ORDER/i.test(direct[1])) return clean(direct[1], 100);
    if (/^INCIDENT\s+NAME\s*[:#-]?$/i.test(line)) {
      const next = lines.slice(index + 1, index + 4).find(candidate =>
        candidate.length >= 3 && !/^(OPERATIONAL|DATE|TIME|INCIDENT|ORDER|FROM|TO)\b/i.test(candidate)
      );
      if (next) return clean(next, 100);
    }
  }
  return '';
}

function normalizeDate(value) {
  const match = String(value || '').match(/\b(\d{1,2})[\/.\-](\d{1,2})[\/.\-](\d{2,4})\b/);
  if (!match) return '';
  let year = Number(match[3]);
  if (year < 100) year += 2000;
  return `${Number(match[1])}/${Number(match[2])}/${year}`;
}

function normalizeTime(value) {
  const match = String(value || '').match(/\b([01]?\d|2[0-3])[:.]?(\d{2})\b/);
  if (!match) return '';
  return `${String(Number(match[1])).padStart(2, '0')}${match[2]}`;
}

function operationalPeriodFromText(text) {
  const source = String(text || '');
  const upper = source.toUpperCase();
  const index = upper.indexOf('OPERATIONAL PERIOD');
  const sample = index >= 0 ? source.slice(index, index + 1600) : source.slice(0, 2400);
  const datePattern = /\b\d{1,2}[\/.\-]\d{1,2}[\/.\-]\d{2,4}\b/g;
  const dates = [...sample.matchAll(datePattern)].map(match => normalizeDate(match[0])).filter(Boolean);
  // Remove full dates before looking for compact 24-hour times so a year such as
  // 2026 is never misread as 20:26.
  const timeSample = sample.replace(datePattern, ' ');
  const times = [...timeSample.matchAll(/\b(?:[01]?\d|2[0-3])[:.]?\d{2}\b/g)].map(match => normalizeTime(match[0])).filter(Boolean);

  const uniqueDates = [...new Set(dates)];
  if (uniqueDates.length >= 2) {
    return `${uniqueDates[0]}${times[0] ? ` ${times[0]}` : ''} – ${uniqueDates[1]}${times[1] ? ` ${times[1]}` : ''}`;
  }
  if (uniqueDates.length === 1) {
    if (times.length >= 2) return `${uniqueDates[0]} ${times[0]}–${times[1]}`;
    return `${uniqueDates[0]}${times[0] ? ` ${times[0]}` : ''}`;
  }
  return '';
}

function dateFromFilename(filename) {
  const value = String(filename || '');
  const ymd = value.match(/\b(20\d{2})[-_.]?(0[1-9]|1[0-2])[-_.]?([0-2]\d|3[01])\b/);
  if (ymd) return `${Number(ymd[2])}/${Number(ymd[3])}/${ymd[1]}`;
  const mdY = value.match(/\b(0?[1-9]|1[0-2])[-_.]([0-2]?\d|3[01])[-_.](20\d{2}|\d{2})\b/);
  if (mdY) {
    const year = Number(mdY[3]) < 100 ? 2000 + Number(mdY[3]) : Number(mdY[3]);
    return `${Number(mdY[1])}/${Number(mdY[2])}/${year}`;
  }
  return '';
}

export function deriveIapMetadata({ sourceUrl = '', documentUrl = '', filename = '', textPages = [] } = {}) {
  const allText = (Array.isArray(textPages) ? textPages : []).join('\n');
  const urlMetadata = wildfireIncidentFromUrl(documentUrl) || wildfireIncidentFromUrl(sourceUrl) || {};
  const incidentOrder = urlMetadata.incidentOrder || incidentOrderFromText(allText);
  const incidentName = urlMetadata.incidentName || incidentNameFromText(allText);
  const operationalPeriod = operationalPeriodFromText(allText) || dateFromFilename(filename);

  return {
    incidentName: clean(incidentName, 100),
    incidentOrder: clean(incidentOrder, 40),
    operationalPeriod: clean(operationalPeriod, 120),
  };
}

export function incidentKeyFor(record) {
  const order = clean(record?.incidentOrder, 50).toUpperCase();
  if (order) return order;
  return clean(record?.incidentName, 120).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'saved-iap';
}

export function safeIapDisplayName(record) {
  const name = clean(record?.incidentName, 100) || 'Incident IAP';
  const period = clean(record?.operationalPeriod, 120);
  return period ? `${name} · ${period}` : name;
}
